/**
 * Report data assembly — resolves the builder's scope/period selections into the
 * data each report section needs, and composes the executive narrative.
 */
import { oeBand } from './constants';
import { normalizeThresholds } from './bands';
import { listTRLs } from '../../../services/trlService';
import { getVisibleCaseStudies } from '../../../services/documentService';
import { timestampToDate } from '../../../utils/firestore';
import { CASE_STUDY_CATEGORY_LABELS, caseStudyCategory, benchmarkDelta } from './benchmark';
import { includesIndustryLibrary } from './libraryPrefs';

const QUADS = ['reliability', 'efficiency', 'productivity', 'safety'];
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

export const REPORT_PERIODS = [
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days (quarter)' },
  { value: 'last365', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Specific period…' }, // P5-03: date range picker in the builder
];

export function periodCutoff(p) {
  const d = new Date();
  if (p === 'last30') { d.setDate(d.getDate() - 30); return d; }
  if (p === 'last90') { d.setDate(d.getDate() - 90); return d; }
  if (p === 'last365') { d.setFullYear(d.getFullYear() - 1); return d; }
  return null; // all time / custom (handled by periodRange)
}

/**
 * The [from, to] window for a period. `custom` uses `range = { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }`
 * (to is inclusive — the whole day). Either bound may be missing.
 */
export function periodRange(p, range = null) {
  if (p === 'custom') {
    const from = range?.from ? new Date(`${range.from}T00:00:00`) : null;
    const to = range?.to ? new Date(`${range.to}T23:59:59.999`) : null;
    return { from: from && !Number.isNaN(from.getTime()) ? from : null, to: to && !Number.isNaN(to.getTime()) ? to : null };
  }
  return { from: periodCutoff(p), to: null };
}

export function periodLabelFor(p, range = null) {
  if (p === 'custom') return `${range?.from || '…'} → ${range?.to || '…'}`;
  return (REPORT_PERIODS.find((x) => x.value === p) || {}).label || p;
}

/**
 * Case-Study Benchmark builder options (Q-03, Tim's Aug 25 message): pick specific
 * library documents by category (checkboxes + select-all per category) and benchmark
 * the scope's REPS scores against just those. `documentIds` empty → fall back to the
 * filter knobs (source / industry / quadrant / limit).
 */
export const CASE_STUDY_OPTION_DEFAULTS = Object.freeze({ source: 'all', industry: '', quadrant: '', limit: 5, documentIds: [] });

export { CASE_STUDY_CATEGORY_LABELS, CASE_STUDY_CATEGORY_ORDER, caseStudyCategory, groupCaseStudiesByCategory, benchmarkDelta } from './benchmark';
export const CASE_STUDY_SOURCES = [
  { value: 'all', label: 'Industry library + my organization' },
  { value: 'global', label: 'Industry library only' },
  { value: 'org', label: 'My organization only' },
];

/**
 * Scope options for the builder dropdown (Enterprise → Region → Site → Dept → Team).
 * Pass the live `comparisons` object (from useSiteComparisons) to build options
 * from the active organization's real data; without it only Enterprise-wide is offered.
 */
export function buildScopeOptions(comparisons = null, queryScope = null) {
  // Scoped members only get their own branch of the org as report scopes.
  const lockSiteId = queryScope?.siteId || null;
  const lockDeptId = queryScope?.deptId || null;
  const lockTeamId = queryScope?.nwtId || null;
  const locked = Boolean(lockSiteId || lockDeptId || lockTeamId);

  const opts = [];
  if (!locked) opts.push({ value: 'enterprise', label: 'Enterprise-wide', type: 'enterprise' });
  if (!comparisons) return opts.length ? opts : [{ value: 'enterprise', label: 'Enterprise-wide', type: 'enterprise' }];

  if (!locked) {
    comparisons.regions.forEach((r) => opts.push({ value: r.id, label: r.name, type: 'region' }));
  }
  comparisons.siteScores
    .filter((s) => !lockSiteId || s.siteId === lockSiteId)
    .forEach((s) => opts.push({ value: s.siteId, label: `${s.siteName} (site)`, type: 'site' }));
  comparisons.deptScores
    .filter((d) => (lockDeptId ? d.deptId === lockDeptId : !lockSiteId || d.siteId === lockSiteId))
    .forEach((d) => {
      const site = comparisons.siteScores.find((s) => s.siteId === d.siteId);
      opts.push({ value: d.deptId, label: `${d.deptName}${site ? ` — ${site.siteName}` : ''} (dept)`, type: 'dept' });
    });
  const lockedDeptIds = lockDeptId
    ? new Set([lockDeptId])
    : lockSiteId
      ? new Set(comparisons.deptScores.filter((d) => d.siteId === lockSiteId).map((d) => d.deptId))
      : null;
  comparisons.teamScores
    .filter((t) => (lockTeamId ? t.teamId === lockTeamId : !lockedDeptIds || lockedDeptIds.has(t.departmentId)))
    .forEach((t) => opts.push({ value: t.teamId, label: `${t.teamName} (team)`, type: 'nwt' }));

  // Never return an empty list — fall back to the most specific locked unit.
  return opts.length ? opts : [{ value: 'enterprise', label: 'Enterprise-wide', type: 'enterprise' }];
}

/** Resolve a scope value to its REPS scores + label + type (live data only). */
export function resolveScope(value, comparisons = null) {
  if (!comparisons) return { scores: null, label: value, type: 'unknown', trend: null };
  if (value === 'enterprise') {
    return { scores: comparisons.enterpriseAverage, label: comparisons.org?.name || 'Enterprise-wide', type: 'enterprise', trend: null };
  }
  const region = comparisons.regions.find((r) => r.id === value);
  if (region) return { scores: comparisons.regionalAverages[value], label: region.name, type: 'region', trend: null };
  const site = comparisons.siteScores.find((s) => s.siteId === value);
  if (site) return { scores: site.scores, label: site.siteName, type: 'site', trend: site.trend };
  const dept = comparisons.deptScores.find((d) => d.deptId === value);
  if (dept) return { scores: dept.scores, label: dept.deptName, type: 'dept', trend: null };
  const team = comparisons.teamScores.find((t) => t.teamId === value);
  if (team) return { scores: team.scores, label: team.teamName, type: 'nwt', trend: null };
  return { scores: null, label: value, type: 'unknown', trend: null };
}

/** Does a TRL fall within the chosen scope? */
export function trlInScope(trl, scopeValue, scopeType, comparisons = null) {
  if (scopeType === 'enterprise') return true;
  if (scopeType === 'region') {
    const siteIds = (comparisons?.regions.find((r) => r.id === scopeValue)?.sites || []).map((s) => s.siteId);
    return siteIds.includes(trl.siteId);
  }
  if (scopeType === 'site') return trl.siteId === scopeValue;
  if (scopeType === 'dept') return trl.deptId === scopeValue;
  if (scopeType === 'nwt') return trl.nwtId === scopeValue;
  return true;
}

/** Compose a short executive narrative from the resolved scores + TRLs. */
export function composeNarrative({ scopeLabel, periodLabel, scores, trls, comparisons = null, scopeType = null, thresholds = null }) {
  if (!scores) return '';
  const tN = normalizeThresholds(thresholds);
  const avgThreshold = QUADS.reduce((s, q) => s + tN[q], 0) / QUADS.length;
  const avg = QUADS.reduce((s, q) => s + scores[q], 0) / QUADS.length;
  const overall = oeBand(avg, avgThreshold);

  // Priority quadrant: severity-IV TRLs outrank a 0.1 score difference.
  // (A bypassed interlock beats "Reliability is 5.8 vs Safety's 5.8".)
  const sevIV = trls.filter((t) => (t.severityDH || t.severityFLS || t.severityITM) === 'IV');
  let worst = QUADS.reduce((a, b) => (scores[b] > scores[a] ? b : a), QUADS[0]);
  let worstReason = `at ${scores[worst].toFixed(1)} (${oeBand(scores[worst], tN[worst]).label.toLowerCase()})`;
  if (sevIV.length > 0) {
    const votes = {};
    sevIV.forEach((t) => { if (t.repsQuadrant) votes[t.repsQuadrant] = (votes[t.repsQuadrant] || 0) + 1; });
    const topQuad = Object.keys(votes).sort((a, b) => votes[b] - votes[a])[0];
    if (topQuad) {
      worst = topQuad;
      worstReason = `carrying ${votes[topQuad]} severity-IV ${votes[topQuad] === 1 ? 'item' : 'items'} on top of a ${(scores[topQuad] ?? 0).toFixed(1)} score`;
    }
  }

  const p1 = `Over the ${periodLabel.toLowerCase()}, ${scopeLabel} shows an overall organizational-entropy reading of ${avg.toFixed(1)} out of 10 — ${overall.label.toLowerCase()}. The strongest pressure sits in ${cap(worst)}, ${worstReason}, which is where the next intervention will create the most leverage.`;

  // Hotspots: an org-wide average hides local fires — name them.
  let pHotspots = '';
  if (comparisons?.siteScores?.length && (scopeType === 'enterprise' || scopeType === 'region')) {
    const hot = [...comparisons.siteScores]
      .filter((s) => s.overall >= 6)
      .sort((a, b) => b.overall - a.overall)
      .slice(0, 3);
    if (hot.length) {
      pHotspots = `The aggregate hides concentration: ${hot
        .map((s) => `${s.siteName} (${s.overall.toFixed(1)})`)
        .join(', ')} ${hot.length === 1 ? 'sits' : 'sit'} in the warning band and ${hot.length === 1 ? 'accounts' : 'account'} for most of the pressure.`;
    }
  }

  // Direction of travel vs ~30 days ago.
  let pTrend = '';
  if (comparisons?.siteScores?.length) {
    const worsening = comparisons.siteScores.filter((s) => s.trend === 'worsening');
    const improving = comparisons.siteScores.filter((s) => s.trend === 'improving').length;
    if (worsening.length || improving) {
      const worstMover = worsening.sort((a, b) => b.overall - a.overall)[0];
      pTrend = `Direction of travel vs. roughly 30 days ago: ${worsening.length} site${worsening.length === 1 ? '' : 's'} worsening, ${improving} improving${worstMover ? ` — ${worstMover.siteName} is the priority mover` : ''}.`;
    }
  }

  const open = trls.filter((t) => !['approved', 'resolved', 'rejected'].includes(t.status)).length;
  const crit = sevIV.length;
  const p2 = trls.length
    ? `${trls.length} OE ${trls.length === 1 ? 'Condition was' : 'Conditions were'} logged in this period${crit ? `, including ${crit} at the highest severity (IV)` : ''}. ${open > 0 ? `${open} remain open and awaiting action.` : 'All have confirmed Reverse / Eliminate / Fortify actions in progress.'}`
    : `No OE Conditions were logged in this period for this scope — worth confirming that reporting is actually happening on the floor rather than assuming all-clear.`;

  const p3 = `Recommendation: prioritize ${cap(worst)}. Review the open items, confirm Reverse / Eliminate / Fortify actions with named owners and due dates, and re-survey at the end of the next cycle to verify the score is trending down.`;

  return [p1, pHotspots, pTrend, p2, p3].filter(Boolean).join('\n\n');
}

/**
 * Assemble a full report model from a config — shared by the builder and the
 * one-click presets. Pulls live TRLs + case studies; resolves scope + narrative.
 * @param {{ sel:object, scopeValue:string, period:string, title?:string, personaLabel?:string }} cfg
 */
export async function assembleReport({ sel, scopeValue, period, range = null, title, personaLabel, comparisons = null, organizationId = null, thresholds = null, caseStudyOptions = null, org = null }) {
  const tN = normalizeThresholds(thresholds); // P5-01: bands in reports follow the org's thresholds
  const { scores, label, type, trend } = resolveScope(scopeValue, comparisons);
  const periodLabel = periodLabelFor(period, range);
  const { from: cutoff, to: until } = periodRange(period, range);

  // Fetch TRLs whenever the narrative OR the log section needs them — the two
  // previously disagreed ("3 TRLs logged" vs "none logged") because the
  // narrative only saw TRLs when the log section happened to be selected.
  //
  // CROSS-TENANT GUARD, deny-by-default: TRLs are only fetched when we know
  // which organization to fence to. The explicit organizationId param is the
  // source of truth (comparisons can still be loading when the user clicks
  // Generate — an earlier version fenced off comparisons.org.id and leaked
  // other orgs' TRLs during that loading window).
  let trls = [];
  const orgFence = organizationId || comparisons?.org?.id || null;
  if ((sel.trls || sel.narrative) && orgFence) {
    const all = await listTRLs({ organizationId: orgFence }, { limit: 500 });
    trls = all.filter(
      (t) =>
        t.organizationId === orgFence &&
        trlInScope(t, scopeValue, type, comparisons) &&
        (!cutoff || (timestampToDate(t.submittedAt) || new Date(0)) >= cutoff) &&
        (!until || (timestampToDate(t.submittedAt) || new Date(0)) <= until)
    );
  }

  let caseStudies = [];
  if (sel.caseStudies) {
    const opt = { ...CASE_STUDY_OPTION_DEFAULTS, ...(caseStudyOptions || {}) };
    const cs = await getVisibleCaseStudies(orgFence || null, org ? { includeIndustryLibrary: includesIndustryLibrary(org) } : {});
    const picked = new Set((opt.documentIds || []).filter(Boolean));
    // Library documents carry their REPS impact as `repsMapping` (0 = no impact, 10 = critical); older seeds used `repsScores`.
    const docScores = (c) => c.repsMapping || c.repsScores || null;
    caseStudies = cs
      .filter((c) => ['ready', 'published'].includes(c.status) || !c.status)
      .filter((c) => (picked.size ? picked.has(c.id) : true))
      .filter((c) => (picked.size ? true : opt.source === 'global' ? c.visibility !== 'org_only' && !c.organizationId : opt.source === 'org' ? Boolean(c.organizationId) && (!orgFence || c.organizationId === orgFence) : true))
      .filter((c) => (picked.size || !opt.industry ? true : String(c.industry || '').toLowerCase().includes(String(opt.industry).toLowerCase())))
      .filter((c) => (picked.size || !opt.quadrant ? true : (docScores(c)?.[opt.quadrant] != null || (c.tags || []).some((tg) => String(tg).toLowerCase().includes(opt.quadrant)))))
      .sort((a, b) => (!picked.size && opt.quadrant ? (docScores(b)?.[opt.quadrant] || 0) - (docScores(a)?.[opt.quadrant] || 0) : 0))
      .slice(0, picked.size ? picked.size : Math.max(1, Math.min(20, Number(opt.limit) || 5)))
      .map((c) => ({ id: c.id, title: c.title, industry: c.industry, category: caseStudyCategory(c), categoryLabel: CASE_STUDY_CATEGORY_LABELS[caseStudyCategory(c)], tags: c.tags || [], repsScores: docScores(c), source: c.organizationId ? 'org' : 'global', delta: benchmarkDelta(scores, docScores(c)) }));
  }

  const narrative = sel.narrative
    ? composeNarrative({ scopeLabel: label, periodLabel, scores, trls, comparisons, scopeType: type, thresholds: tN })
    : '';

  return {
    title: (title && title.trim()) || `${label} — REPS Report`,
    scopeLabel: label,
    periodLabel,
    personaLabel: personaLabel || 'REPSit',
    generatedAt: new Date().toLocaleString(),
    period,
    range: period === 'custom' ? range : null,
    org: org ? { id: org.id, departments: org.departments || [], teams: org.teams || [] } : null,
    sections: {
      performance: { include: sel.performance, scores, trend, thresholds: tN },
      narrative: { include: sel.narrative, text: narrative },
      trls: { include: sel.trls, items: trls },
      caseStudies: { include: sel.caseStudies, items: caseStudies, options: caseStudyOptions || null, picked: Boolean((caseStudyOptions?.documentIds || []).length), scopeScores: scores || null },
    },
  };
}

/**
 * One-click Quick Report presets. All scope 'enterprise' (live-safe for any org);
 * open any preset in the builder to narrow the scope.
 */
export const REPORT_PRESETS = [
  {
    id: 'reps_summary',
    title: 'REPS Performance Summary',
    desc: 'Organizational overview of current REPS Quadrant Scores, including an Executive Briefing',
    includes: ['Performance', 'Executive Briefing'],
    config: {
      sel: { performance: true, narrative: true, trls: false, caseStudies: false },
      scopeValue: 'enterprise',
      period: 'last30',
      title: 'REPS Performance Summary',
    },
  },
  {
    id: 'trl_action_log',
    title: 'OE Condition & REF Action Log',
    desc: 'Each OE Condition logged during the period, including severities, REPS quadrant(s), and REF remediation actions planned/completed.',
    includes: ['OE Conditions', 'REF actions'],
    config: {
      sel: { performance: false, narrative: false, trls: true, caseStudies: false },
      scopeValue: 'enterprise',
      period: 'last90',
      title: 'OE Condition & REF Action Log',
    },
  },
  {
    id: 'exec_briefing',
    title: 'Executive Briefing',
    desc: 'REPS performance, executive overview, and a table of OE Conditions identified during the report period, including their current status',
    includes: ['Performance', 'Executive Briefing', 'OE Conditions'],
    config: {
      sel: { performance: true, narrative: true, trls: true, caseStudies: false },
      scopeValue: 'enterprise',
      period: 'last30',
      title: 'Executive Briefing',
    },
  },
  {
    id: 'benchmark_pack',
    // Tim (D09 §2.5): "Case-Study Benchmark", Report-Builder only — the Quick Report card is hidden
    // via `builderOnly` until ⚑Q-03 settles the builder options.
    builderOnly: true,
    title: 'Case-Study Benchmark',
    desc: 'Your performance alongside analyzed industry case studies for context.',
    includes: ['Performance', 'Case studies'],
    config: {
      sel: { performance: true, narrative: false, trls: false, caseStudies: true },
      scopeValue: 'enterprise',
      period: 'last90',
      title: 'Case-Study Benchmark',
    },
  },
];
